<template>
  <div class="mod-package-price">
    <div class="page-header">
      <span class="page-header__title">套餐定价</span>
    </div>

    <div class="page-body" v-loading="loading">
      <div class="toolbar">
        <span class="toolbar__desc">配置 H5 购买弹窗中的套餐选项，带高亮的为已设为默认 / 推荐的套餐</span>
        <div class="toolbar__right">
          <el-button type="primary" icon="el-icon-plus" @click="addOrUpdate()">新增套餐</el-button>
        </div>
      </div>

      <!-- 套餐卡片预览 -->
      <div class="package-grid">
        <div
          v-for="item in enabledList"
          :key="item.id"
          class="package-card"
          :class="{ 'is-recommend': item.recommend }"
          @click="addOrUpdate(item.id)">
          <div class="package-card__name">{{ item.shortName }}</div>
          <div class="package-card__price">¥{{ formatPrice(item.price) }}</div>
          <div class="package-card__desc">{{ item.spec }} · {{ item.scope }}</div>
        </div>
        <div v-if="!enabledList.length && !loading" class="empty-tip">暂无已开启的套餐</div>
      </div>

      <!-- 套餐列表 -->
      <div class="panel">
        <el-table :data="dataList" style="width: 100%;">
          <el-table-column prop="commodityToSort" label="排序" width="90"></el-table-column>
          <el-table-column prop="duration" label="展示文案" min-width="160"></el-table-column>
          <el-table-column label="价格" width="120">
            <template slot-scope="scope">¥{{ formatPrice(scope.row.price) }}</template>
          </el-table-column>
          <el-table-column label="类型" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.type === 2 ? 'success' : ''" size="small">
                {{ commodityTypeMap[scope.row.type] || '—' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="有效时长" min-width="120">
            <template slot-scope="scope">{{ scope.row.spec }}</template>
          </el-table-column>
          <el-table-column label="权益范围" width="110">
            <template slot-scope="scope">{{ scope.row.scope }}</template>
          </el-table-column>
          <el-table-column label="推荐位" min-width="180">
            <template slot-scope="scope">
              <el-tag
                v-for="tag in scope.row.defaultTags"
                :key="tag"
                size="mini"
                type="warning"
                effect="plain"
                class="default-tag">
                ★ {{ tag }}
              </el-tag>
              <span v-if="!scope.row.defaultTags.length">—</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === commodityStatus.ENABLED ? 'success' : 'info'" size="small">
                {{ scope.row.status === commodityStatus.ENABLED ? '启用' : '关闭' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right" class-name="operation-cell">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="addOrUpdate(scope.row.id)">编辑</el-button>
              <el-dropdown trigger="click" @command="cmd => handleSetDefault(scope.row, cmd)">
                <el-button type="text" size="small">
                  设为默认<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-for="option in defaultTypeOptions"
                    :key="option.value"
                    :command="option.value"
                    :disabled="scope.row[option.field] === 1">
                    {{ option.label }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-button type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 弹窗：新增 / 修改套餐（复用商品模块） -->
    <add-or-update v-if="dialogVisible" ref="addOrUpdate" @refreshDataList="loadList"></add-or-update>
  </div>
</template>

<script>
import AddOrUpdate from '../commodity/commodity-add-or-update'
import {
  getCommodityPage,
  setCommodityDefault,
  deleteCommodity,
  COMMODITY_SPEC_MAP,
  COMMODITY_TYPE_MAP,
  COMMODITY_STATUS,
  DEFAULT_TYPE_OPTIONS
} from '@/api/trade'

// 套餐数量有限，一次性拉取用于卡片预览
const FETCH_LIMIT = 200
// VIP 类型
const TYPE_VIP = 2

export default {
  name: 'PackagePrice',
  components: {
    AddOrUpdate
  },
  data () {
    return {
      loading: false,
      dataList: [],
      dialogVisible: false
    }
  },

  computed: {
    commodityTypeMap () {
      return COMMODITY_TYPE_MAP
    },
    commodityStatus () {
      return COMMODITY_STATUS
    },
    defaultTypeOptions () {
      return DEFAULT_TYPE_OPTIONS
    },
    // 卡片区只展示已开启的套餐
    enabledList () {
      return this.dataList.filter(item => item.status === COMMODITY_STATUS.ENABLED)
    }
  },

  created () {
    this.loadList()
  },

  methods: {
    loadList () {
      this.loading = true
      return getCommodityPage({ page: 1, limit: FETCH_LIMIT }).then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        const list = (res.data && res.data.list) || []
        const total = (res.data && res.data.total) || 0
        if (total > FETCH_LIMIT) {
          this.$message.warning(`套餐共 ${total} 条，当前只展示前 ${FETCH_LIMIT} 条`)
        }
        this.dataList = list
          .map(item => this.decorate(item))
          .sort((a, b) => (Number(a.commodityToSort) || 0) - (Number(b.commodityToSort) || 0))
      }).catch(() => {
        this.$message.error('加载套餐失败')
      }).finally(() => {
        this.loading = false
      })
    },

    // 补齐展示用的派生字段
    decorate (item) {
      const defaultTags = DEFAULT_TYPE_OPTIONS
        .filter(option => item[option.field] === 1)
        .map(option => option.label)
      return {
        ...item,
        shortName: this.shortName(item.duration),
        // 规格已改为编码，历史数据可能是「30天」这类文本，映射不到时原样展示
        spec: COMMODITY_SPEC_MAP[item.commoditySpecification] || item.commoditySpecification || '—',
        // 后端无「权益范围」字段，按商品类型推导：VIP 为全站，钻石为单视频
        scope: item.type === TYPE_VIP ? '全站' : '单视频',
        defaultTags,
        recommend: defaultTags.length > 0
      }
    },

    // 展示文案里通常带价格（如「包月¥68」），卡片标题去掉价格部分
    shortName (duration) {
      const name = String(duration || '').replace(/[¥￥]\s*[\d.]+/g, '').trim()
      return name || duration || '—'
    },

    addOrUpdate (id) {
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.addOrUpdate.dataForm.id = id || ''
        this.$refs.addOrUpdate.init()
      })
    },

    handleSetDefault (row, defaultType) {
      const option = DEFAULT_TYPE_OPTIONS.find(item => item.value === defaultType)
      this.$confirm(`确定把「${row.duration}」设为${option.label}的默认套餐？`, '提示', {
        type: 'warning'
      }).then(() => {
        return setCommodityDefault(row.id, defaultType)
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '设置失败'))
        }
        this.$message.success('设置成功')
        this.loadList()
      }).catch(e => {
        if (e && e.message) {
          this.$message.error(e.message)
        }
      })
    },

    // 删除接口是批量的，单个删除也传数组
    handleDelete (row) {
      const isDefault = row.defaultTags.length
      const tip = isDefault
        ? `「${row.duration}」当前是${row.defaultTags.join('、')}的默认套餐，删除后这些位置将没有默认套餐。确定删除？`
        : `确定删除套餐「${row.duration}」？`
      this.$confirm(tip, '提示', {
        type: 'warning'
      }).then(() => {
        return deleteCommodity([row.id])
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '删除失败'))
        }
        this.$message.success('删除成功')
        this.loadList()
      }).catch(e => {
        if (e && e.message) {
          this.$message.error(e.message)
        }
      })
    },

    formatPrice (value) {
      const num = Number(value || 0)
      return Number.isNaN(num) ? '0.00' : num.toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-package-price {
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.page-body {
  padding: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  &__desc {
    font-size: 13px;
    color: #909399;
  }

  &__right {
    margin-left: auto;
  }
}

.package-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.package-card {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s;

  &:hover {
    border-color: #409eff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, .15);
  }

  &.is-recommend {
    border-color: #409eff;
    background-color: #f2f7ff;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__price {
    font-size: 22px;
    font-weight: 700;
    color: #1a5cf0;
    margin: 12px 0;
  }

  &__desc {
    font-size: 12px;
    color: #909399;
  }
}

.empty-tip {
  grid-column: 1 / -1;
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 20px 0;
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 0 16px 16px;
}

.default-tag {
  margin-right: 6px;
}

.danger-text {
  color: #f56c6c;
}

// 操作列的按钮均分排列，只作用于该列不影响其它单元格
::v-deep .operation-cell .cell {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

::v-deep .el-table th {
  background-color: #fafbfc;
  color: #909399;
  font-weight: 500;
}

@media screen and (max-width: 1200px) {
  .package-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
